import type { ParsedLarkData } from '@/types';

/**
 * Parse Lark chat records
 * Format: MMDD systolic/diastolic [bloodSugar]
 * Example: 0306 141/89 6.2
 */
export function parseLarkText(text: string): ParsedLarkData[] {
  const lines = text.split('\n').filter(line => line.trim());
  const results: ParsedLarkData[] = [];
  
  const pattern = /^(\d{2})(\d{2})\s+(\d{3})\/(\d{2,3})(?:\s+(\d+\.?\d*))?$/;

  for (const line of lines) {
    const trimmed = line.trim();
    const match = trimmed.match(pattern);
    
    if (match) {
      const month = parseInt(match[1], 10);
      const day = parseInt(match[2], 10);
      const systolic = parseInt(match[3], 10);
      const diastolic = parseInt(match[4], 10);
      const bloodSugar = match[5] ? parseFloat(match[5]) : undefined;

      // Validate date
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        // Infer year based on current date
        const year = inferYear(month, day);
        const date = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        
        results.push({
          date,
          systolic,
          diastolic,
          bloodSugar
        });
      }
    }
  }

  return results;
}

/**
 * Infer year based on current date
 * If record month > current month, it's from last year
 * If record month == current month but day > current day, it's from last year
 */
function inferYear(month: number, day: number): number {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentDay = now.getDate();

  if (month > currentMonth) {
    return currentYear - 1;
  }
  
  if (month === currentMonth && day > currentDay) {
    return currentYear - 1;
  }
  
  return currentYear;
}

/**
 * Calculate MD5 hash for duplicate detection
 */
export function calculateMD5(input: string): string {
  // Simple hash function for browser use
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString(16);
}
